import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import axios from '../store/axiosInterceptor';
import { DetailPageSkeleton } from '../components/Skeleton';
import '../styles/quill-custom.css';

interface AssignedCoach {
  id: string;
  user_id?: string;
  name: string;
  first_name?: string;
  last_name?: string;
  avatar?: string;
  email: string;
  phone?: string;
  bio?: string;
  zoom_account_linked?: boolean;
  commission_rate?: number;
  approved?: boolean;
  location?: string;
  timezone?: string;
  target_sessions?: number;
  area_of_expertise?: string;
  notes?: string;
  gender?: string;
  linkedin_url?: string;
  instagram_url?: string;
  facebook_url?: string;
  youtube_url?: string;
  twitter_url?: string;
  tiktok_url?: string;
  created_at?: string;
  updated_at?: string;
  status?: string;
  completed_sessions?: number;
}

interface Student {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  first_name: string;
  last_name: string;
  status: string;
  focus_area: string;
  notes: string;
  completed_sessions: number;
  last_session: string | null;
  professional_bio: string;
  location: string;
  timezone: string;
  role: string;
  coach_id?: string;
  assigned_coach?: AssignedCoach;
}

const StudentDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'coach' | 'sessions' | 'reviews' | 'notes'>('coach');
  const [noteContent, setNoteContent] = useState('');
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [notes, setNotes] = useState<Array<{ id: string; date: string; content: string }>>([]);

  useEffect(() => {
    const fetchStudentDetail = async () => {
      try {
        // Fetch specific student using the optimized endpoint
        const { data } = await axios.get(`/api/users/${id}`);
        setStudent(data);
        
        // Fetch notes for the student
        if (id) {
          try {
            const { data: notesData } = await axios.get(`/api/admin/students/${id}/notes`);
            // Transform backend response format to frontend format
            const transformedNotes = (notesData.notes || []).map((note: any) => ({
              id: note.note_id,
              date: new Date(note.created_at).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              }),
              content: note.text,
            }));
            setNotes(transformedNotes);
          } catch (error) {
            console.error('Error fetching notes:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching student:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchStudentDetail();
    }
  }, [id]);

  // // Render stars helper function (currently unused but kept for future use)
  // const renderStars = (rating: number) => {
  //   const stars = [];
  //   const fullStars = Math.floor(rating);
  //   
  //   for (let i = 0; i < 5; i++) {
  //     if (i < fullStars) {
  //       stars.push(
  //         <svg key={i} className="h-5 w-5 fill-orange-400" viewBox="0 0 20 20">
  //           <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  //         </svg>
  //       );
  //     } else {
  //       stars.push(
  //         <svg key={i} className="h-5 w-5 fill-gray-300" viewBox="0 0 20 20">
  //           <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  //         </svg>
  //       );
  //     }
  //   }
  //   
  //   return stars;
  // };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: noteContent,
    onUpdate: ({ editor }) => {
      setNoteContent(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none',
        'data-placeholder': 'Write your note here',
      },
    },
  });

  const handleAddNote = async () => {
    if (noteContent.trim() && noteContent !== '<p></p>') {
      const newNote = {
        id: Date.now().toString(),
        date: new Date().toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric' 
        }),
        content: noteContent,
      };
      
      // Add to local state immediately
      setNotes([newNote, ...notes]);
      setNoteContent('');
      editor?.commands.clearContent();
      setShowNoteEditor(false);

      // Save to backend
      try {
        await axios.post(`/api/admin/students/${id}/notes`, {
          text: [noteContent],
        });
      } catch (error) {
        console.error('Error adding note:', error);
      }
    }
  };

  const handleCancelNote = () => {
    setNoteContent('');
    editor?.commands.clearContent();
    setShowNoteEditor(false);
  };

  if (loading) {
    return <DetailPageSkeleton />;
  }

  if (!student) {
    return (
      <div className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
        <p className="text-gray-500">Student not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Student Profile Card */}
      <div className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
        {/* Back Button */}
        <button
          onClick={() => navigate('/students')}
          className="mb-6 flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-gray-700"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>Student Details</span>
        </button>

        {/* Top Section */}
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-4">
            {student.avatar ? (
              <img 
                src={student.avatar} 
                alt={student.first_name || student.name}
                className="h-16 w-16 rounded-full object-cover"
              />
            ) : (
              <div className="h-16 w-16 rounded-full bg-blue-500 flex items-center justify-center">
                <span className="text-2xl font-semibold text-white">
                  {student.first_name && student.last_name 
                    ? `${student.first_name[0]}${student.last_name[0]}` 
                    : student.name 
                      ? student.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                      : 'S'}
                </span>
              </div>
            )}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {student.first_name && student.last_name 
                  ? `${student.first_name} ${student.last_name}` 
                  : student.name || 'Student'}
              </h2>
              <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                <span>{student.email}</span>
                <div className="flex items-center gap-1">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span>{student.phone}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-[7px] bg-green-50 px-4 py-1.5 text-sm font-medium text-green-700">
              {student.status || 'Active'}
            </span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-6 border-t border-[#F2F4F7] pt-6">
          <div>
            <p className="text-xs text-gray-400 mb-1">Focus area</p>
            <p className="font-medium text-gray-900">{student.focus_area || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Completed Sessions</p>
            <p className="font-medium text-gray-900">{student.completed_sessions}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-[#F2F4F7]">
        <button
          onClick={() => setActiveTab('coach')}
          className={`pb-3 text-sm font-medium transition-colors ${
            activeTab === 'coach'
              ? 'border-b-2 border-gray-900 text-gray-900'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Coach
        </button>
        <button
          onClick={() => setActiveTab('sessions')}
          className={`pb-3 text-sm font-medium transition-colors ${
            activeTab === 'sessions'
              ? 'border-b-2 border-gray-900 text-gray-900'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Sessions
        </button>
        <button
          onClick={() => setActiveTab('reviews')}
          className={`pb-3 text-sm font-medium transition-colors ${
            activeTab === 'reviews'
              ? 'border-b-2 border-gray-900 text-gray-900'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Reviews
        </button>
        <button
          onClick={() => setActiveTab('notes')}
          className={`pb-3 text-sm font-medium transition-colors ${
            activeTab === 'notes'
              ? 'border-b-2 border-gray-900 text-gray-900'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Notes
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'coach' && (
        <div className="space-y-3">
          {student?.assigned_coach ? (
            <div className="flex items-center justify-between rounded-xl border border-[#F2F4F7] bg-white p-4 shadow-[0_4px_12px_0_rgba(249,250,251,1)] hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                {student.assigned_coach.avatar ? (
                  <img
                    src={student.assigned_coach.avatar}
                    alt={student.assigned_coach.name}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                    <span className="text-lg font-semibold text-white">
                      {student.assigned_coach.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-900">{student.assigned_coach.name}</p>
                  <p className="text-sm text-gray-500">{student.assigned_coach.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-8">
                <div className="text-right">
                  <p className="text-xs text-gray-400 mb-1">Area of Expertise</p>
                  <p className="font-medium text-gray-900">{student.assigned_coach.area_of_expertise || '-'}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400 mb-1">Completed Sessions</p>
                  <p className="font-semibold text-gray-900">{student.assigned_coach.completed_sessions || 0}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
              <p className="text-gray-500 text-center py-8">No coach assigned yet</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'sessions' && (
        <div className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
          <p className="text-gray-500 text-center py-8">No sessions yet</p>
        </div>
      )}

      {activeTab === 'reviews' && (
        <div className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
          <p className="text-gray-500 text-center py-8">No reviews yet</p>
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
          {/* Add Note Button */}
          {!showNoteEditor && (
            <button
              onClick={() => setShowNoteEditor(true)}
              className="mb-6 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50"
            >
              + Add Note
            </button>
          )}

          {/* Note Editor */}
          {showNoteEditor && (
            <div className="mb-6 rounded-lg border border-gray-200 bg-white">
              <div className="min-h-[200px] p-4">
                <EditorContent editor={editor} />
              </div>
              <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-base font-bold transition-colors ${
                      editor?.isActive('bold')
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    B
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-base italic transition-colors ${
                      editor?.isActive('italic')
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    I
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleUnderline().run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-base underline transition-colors ${
                      editor?.isActive('underline')
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    U
                  </button>
                  <span className="h-6 w-px bg-gray-200" />
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-lg transition-colors ${
                      editor?.isActive('bulletList')
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    •
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().setTextAlign('left').run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-base transition-colors ${
                      editor?.isActive({ textAlign: 'left' })
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    ≡
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-base transition-colors ${
                      editor?.isActive({ textAlign: 'center' })
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    ≣
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-base transition-colors ${
                      editor?.isActive({ textAlign: 'right' })
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    ≡
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const url = window.prompt('Enter URL:');
                      if (url) {
                        editor?.chain().focus().setLink({ href: url }).run();
                      }
                    }}
                    className={`flex h-9 w-9 items-center justify-center rounded text-lg transition-colors ${
                      editor?.isActive('link')
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    🔗
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-300 hover:text-gray-900"
                    onClick={handleCancelNote}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`rounded-xl px-4 py-2 text-sm font-semibold text-white transition ${
                      !noteContent.trim() || noteContent === '<p></p>'
                        ? 'bg-gray-200 cursor-not-allowed'
                        : 'bg-gray-900 hover:bg-gray-800'
                    }`}
                    disabled={!noteContent.trim() || noteContent === '<p></p>'}
                    onClick={handleAddNote}
                  >
                    Post
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notes List */}
          {notes.length > 0 ? (
            <div className="flex flex-col gap-6">
              {notes.map((note) => (
                <div key={note.id} className="grid grid-cols-[110px_1fr] gap-4">
                  <span className="text-xs font-semibold text-gray-400">
                    {note.date}
                  </span>
                  <div className="markdown-data text-sm leading-relaxed font-medium text-[#0F0F0F]">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                    >
                      {note.content}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !showNoteEditor && (
              <p className="text-gray-500 text-center py-8">No notes yet</p>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default StudentDetailsPage;
